import type { SvgIconProps } from '@mui/material/SvgIcon';
import { isRecord } from '@openx/components-core/lib/Markdown/widgets/answerWidgetPayload';
import type { ComponentType } from 'react';
import { OPENX_AGENT_PERSONA_AVATARS_STORAGE_KEY } from '../../../../context/consts';
import { OpenIQPersona1Icon } from '../../../icons/personas/OpenIQPersona1Icon';
import { OpenIQPersona1InactiveIcon } from '../../../icons/personas/OpenIQPersona1InactiveIcon';
import { OpenIQPersona2Icon } from '../../../icons/personas/OpenIQPersona2Icon';
import { OpenIQPersona2InactiveIcon } from '../../../icons/personas/OpenIQPersona2InactiveIcon';
import { OpenIQPersona3Icon } from '../../../icons/personas/OpenIQPersona3Icon';
import { OpenIQPersona3InactiveIcon } from '../../../icons/personas/OpenIQPersona3InactiveIcon';
import { OpenIQPersona4Icon } from '../../../icons/personas/OpenIQPersona4Icon';
import { OpenIQPersona4InactiveIcon } from '../../../icons/personas/OpenIQPersona4InactiveIcon';
import { OpenIQPersona5Icon } from '../../../icons/personas/OpenIQPersona5Icon';
import { OpenIQPersona5InactiveIcon } from '../../../icons/personas/OpenIQPersona5InactiveIcon';
import { StatusTone } from '../../utils/statusTonePalette';
import { getPersonaTone, type PersonaStatus } from './personasPayload';

type AvatarIcon = ComponentType<SvgIconProps>;

type PersonaAvatar = { matching: AvatarIcon; nonMatching: AvatarIcon };

const FIRST_PERSONA_AVATAR: PersonaAvatar = { matching: OpenIQPersona1Icon, nonMatching: OpenIQPersona1InactiveIcon };

const PERSONA_AVATARS: PersonaAvatar[] = [
  FIRST_PERSONA_AVATAR,
  { matching: OpenIQPersona2Icon, nonMatching: OpenIQPersona2InactiveIcon },
  { matching: OpenIQPersona3Icon, nonMatching: OpenIQPersona3InactiveIcon },
  { matching: OpenIQPersona4Icon, nonMatching: OpenIQPersona4InactiveIcon },
  { matching: OpenIQPersona5Icon, nonMatching: OpenIQPersona5InactiveIcon },
];

// keeps the picks for the page's lifetime when sessionStorage is unavailable
const assignedInMemory = new Map<string, number>();

const isAvatarIndex = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < PERSONA_AVATARS.length;

const readStoredAssignments = (): Record<string, unknown> => {
  try {
    const parsed: unknown = JSON.parse(window.sessionStorage.getItem(OPENX_AGENT_PERSONA_AVATARS_STORAGE_KEY) ?? '{}');
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

// avatars are random but must persist during a user session: picked once per persona, then reused
export const getPersonaAvatarIndex = (personaId: string): number => {
  const stored = readStoredAssignments()[personaId];
  if (isAvatarIndex(stored)) return stored;

  const inMemory = assignedInMemory.get(personaId);
  if (inMemory !== undefined) return inMemory;

  const index = Math.floor(Math.random() * PERSONA_AVATARS.length);
  assignedInMemory.set(personaId, index);

  try {
    window.sessionStorage.setItem(
      OPENX_AGENT_PERSONA_AVATARS_STORAGE_KEY,
      JSON.stringify({ ...readStoredAssignments(), [personaId]: index })
    );
  } catch {
    // storage unavailable, the in-memory assignment still holds for this page
  }

  return index;
};

export const getPersonaAvatar = (avatarIndex: number, status: PersonaStatus): AvatarIcon => {
  const avatar = PERSONA_AVATARS[avatarIndex] ?? FIRST_PERSONA_AVATAR;
  return getPersonaTone(status) === StatusTone.POSITIVE ? avatar.matching : avatar.nonMatching;
};
